import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { XMarkIcon } from "@heroicons/react/24/outline";

import CommonSlider from "../../../common/CommonSlider";
import {
  isArrayError,
  isFieldError,
  isNameError,
  isValidDateOfBirth,
} from "../../../../helpers/input-validations";
import CustomInput from "../../../common/CustomInput";
import {
  calculateAge,
  formatToYYYYMMDDBackend,
} from "../../../../helpers/functions";
import {
  biologicalSexOptions,
  genderOptions,
  patientInactiveStatusOptions,
  preferencesOptions,
  primaryLanguageOptions,
  patientEditStatusOptions,
  patientEditInactiveStatusReasonOptions,
  characteristicsOptions,
  raceOptions,
  medicationAdministrationOptions,
} from "../../create/options";
import Button from "../../../common/Button";
import { updatePatientPersonalDetails, addClinicalAlertForGastricBypass } from "../../../../redux/modules/patient/actions";
import {
  createMedicalCondition,
  fetchMedicalConditions,
} from "../../../../redux/modules/clinical-services/actions";

const PersonalInformationCreate = ({
  open,
  onClose,
  patient,
  conditions = [],
}) => {
  const dispatch = useDispatch();

  const getStatus = () => {
    if (patient?.status && patient.status === "active") {
      return "active";
    } else {
      return "inactive";
    }
  };

  useEffect(() => {
    dispatch(fetchMedicalConditions(patient.id));
  }, []);

  useEffect(() => {
    setPatientDetails(patient);
  }, [patient]);

  const getStatusReason = (value) => {
    const statusToUse = value && value.length > 0 ? value : patient?.status;
    if (statusToUse && statusToUse !== "active" && statusToUse !== "inactive") {
      return statusToUse;
    } else {
      return "";
    }
  };

  const getPreferenceList = (pref) => {
    return pref?.split(",").map((item) => item.trim()) ?? [];
  };

  const getCharacteristicsList = (char) => {
    return char?.split(",").map((item) => item.trim()) ?? [];
  };

  const normalizeCharacteristicsSelection = (
    previousSelection = [],
    nextSelection = []
  ) => {
    const toValueArray = (collection = []) =>
      collection
        .map((item) => (typeof item === "string" ? item : item?.value))
        .filter(Boolean);

    const prevHasNone = toValueArray(previousSelection).includes("none");
    const nextValues = Array.from(new Set(toValueArray(nextSelection)));

    if (!nextValues.length) {
      return [];
    }

    if (!nextValues.includes("none")) {
      return nextValues;
    }

    if (!prevHasNone) {
      return ["none"];
    }

    const others = nextValues.filter((value) => value !== "none");
    return others.length ? others : ["none"];
  };

  const normalizePreferencesSelection = (
    previousSelection = [],
    nextSelection = []
  ) => {
    const toValueArray = (collection = []) =>
      collection
        .map((item) => (typeof item === "string" ? item : item?.value))
        .filter(Boolean);

    const prevHasNone = toValueArray(previousSelection).includes("none");
    const nextValues = Array.from(new Set(toValueArray(nextSelection)));

    if (!nextValues.length) return [];
    if (!nextValues.includes("none")) return nextValues;
    if (!prevHasNone) return ["none"];

    const others = nextValues.filter((value) => value !== "none");
    return others.length ? others : ["none"];
  };

  const [lastName, setLastName] = useState(patient.lastName);
  const [middleName, setMiddleName] = useState(patient.middleName);
  const [firstName, setFirstName] = useState(patient.firstName);
  const [dateOfBirth, setDateOfBirth] = useState(patient.dateOfBirth);
  const [biologicalSex, setBiologicalSex] = useState(patient.biologicalSex);
  const [gender, setGender] = useState(patient.gender);
  const [race, setRace] = useState(patient.race);
  const [characteristics, setCharacteristics] = useState(
    getCharacteristicsList(patient.characteristics)
  );
  const [medicationAdministration, setMedicationAdministration] = useState(
    patient.medicationAdministration ?? "swallowing"
  );
  const [primaryLanguage, setPrimaryLanguage] = useState(
    patient.primaryLanguage
  );
  const [preferences, setPreferences] = useState(
    getPreferenceList(patient.preferences)
  );
  const [status, setStatus] = useState(getStatus());
  const [statusReason, setStatusReason] = useState(getStatusReason());

  const [lastNameError, setLastNameError] = useState(false);
  const [firstNameError, setFirstNameError] = useState(false);
  const [dateOfBirthError, setDateOfBirthError] = useState(false);
  const [primaryLanguageError, setPrimaryLanguageError] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const [statusReasonError, setStatusReasonError] = useState(false);
  const [characteristicsError, setCharacteristicsError] = useState(false);
  const [administrationError, setAdministrationError] = useState(false);
  const [clicked, setClicked] = useState(false);
  const fieldsToCheck = [
    {
      field: lastName,
      checkFn: isNameError,
      setErrorFn: setLastNameError,
      alter: false,
    },
    {
      field: firstName,
      checkFn: isNameError,
      setErrorFn: setFirstNameError,
      alter: false,
    },
    {
      field: dateOfBirth,
      checkFn: isValidDateOfBirth,
      setErrorFn: setDateOfBirthError,
      alter: true,
    },
    {
      field: primaryLanguage,
      checkFn: isFieldError,
      setErrorFn: setPrimaryLanguageError,
      alter: false,
    },
    {
      field: status,
      checkFn: isFieldError,
      setErrorFn: setStatusError,
      alter: false,
    },
    {
      field: characteristics,
      checkFn: isArrayError,
      setErrorFn: setCharacteristicsError,
      alter: false,
    },
    {
      field: medicationAdministration,
      checkFn: isFieldError,
      setErrorFn: setAdministrationError,
      alter: false,
    },
  ];

  const setPatientDetails = (patient) => {
    if (patient) {
      setLastName(patient.lastName);
      setMiddleName(patient.middleName);
      setFirstName(patient.firstName);
      setDateOfBirth(patient.dateOfBirth);
      setBiologicalSex(patient.biologicalSex);
      setGender(patient.gender);
      setRace(patient.race);
      setCharacteristics(getCharacteristicsList(patient.characteristics));
      setMedicationAdministration(
        patient.medicationAdministration ?? "swallowing"
      );
      setPrimaryLanguage(patient.primaryLanguage);
      setPreferences(getPreferenceList(patient.preferences));
    } else {
      setLastName(null);
      setMiddleName(null);
      setFirstName(null);
      setDateOfBirth(null);
      setBiologicalSex(null);
      setGender(null);
      setRace(null);
      setCharacteristics(null);
      setMedicationAdministration(null);
      setPrimaryLanguage(null);
      setPreferences(null);
    }
  };

  const manageStep = () => {
    setClicked(true);
    let allFieldsValid = true;

    fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
      const hasError = alter ? !checkFn(field) : checkFn(field);
      setErrorFn(hasError);
      if (hasError) {
        allFieldsValid = false;
      }
    });

    // Manually checking for status reason as this is special case.
    // Special case: If we add status reason to fieldsToCheck then when we set status active at that time we have to set reason = ""which will throw error
    if (allFieldsValid) {
      const hasStatusReasonError = status !== "active" && statusReason === "";
      setStatusReasonError(hasStatusReasonError);
      if (hasStatusReasonError) allFieldsValid = false;
    }

    if (allFieldsValid) {
      const newPref =
        preferences && preferences.length > 0 ? preferences.join(", ") : null;
      const newChar =
        characteristics && characteristics.length > 0
          ? characteristics.join(", ")
          : null;
      const transformedObject = {
        firstName: firstName,
        middleName: middleName,
        lastName: lastName,
        dateOfBirth: formatToYYYYMMDDBackend(dateOfBirth),
        biologicalSex:
          biologicalSexOptions?.find((item) => item.value === biologicalSex)
            ?.value || "preferNotToRespond",
        gender:
          genderOptions?.find((item) => item.value === gender)?.value ||
          "preferNotToRespond",
        race: raceOptions?.find((item) => item.value === race)?.value,
        characteristics: newChar || null,
        medicationAdministration: medicationAdministrationOptions?.find(
          (item) => item.value === medicationAdministration
        )?.value,
        status: status !== "active" ? statusReason : status,
        payingCash: false,
        colorBlind: false,
        primaryLanguage: primaryLanguage,
        preferences: newPref || null,
      };
      let onlyStatusChanged =
        firstName === patient.firstName &&
        middleName === patient.middleName &&
        lastName === patient.lastName &&
        dateOfBirth === patient.dateOfBirth &&
        biologicalSex === patient.biologicalSex &&
        gender === patient.gender &&
        primaryLanguage === patient.primaryLanguage &&
        newPref === patient.preferences;
      let statusObject = {
        statusChanged: patient.status !== status,
        oldStatus: patient.status,
        newStatus: status,
        onlyStatusChanged: onlyStatusChanged,
      };

      manageMedicalCondistion(characteristics);
      dispatch(
        updatePatientPersonalDetails(
          patient.id,
          transformedObject,
          statusObject
        )
      );
      onClose(false);
    }
  };

  const manageMedicalCondistion = (characteristics) => {
    const isOstomyExist = conditions.some(
      (val) => val.status === "active" && val.conditionName.includes("ostomy")
    );
    if (!isOstomyExist && characteristics.includes("ostomy"))
      addMedicalCondition("ostomy", "STO_");
    // Dialysis should be added as a surgery/procedure, not a medical condition
    const isDialysisExist = conditions.some(
      (val) => val.status === "active" && val.conditionName.includes("dialysis")
    );
    if (!isDialysisExist && characteristics.includes("dialysis"))
      addMedicalCondition("dialysis", "PRC_");
    // If Gastric Bypass newly added via edit, auto-create Clinical Alert and Surgery/Procedure
    if (characteristics.includes("gastricBypass")) {
      // suppress clinical-alert toast when updating from the patient edit flow
      dispatch(addClinicalAlertForGastricBypass(patient.id, { showToast: false }));
    }
  };

  const addMedicalCondition = (cond, code) => {
    // Backend now stores symptomControlled as string; use null to show "Not Specified" by default
    let currentCondition = {
      medicalConditions: [
        {
          name: cond,
          medicalConditionGenCode: code,
          // For characteristics-driven medical conditions, default to "Not Specified" (UI shows this when symptomControlled is null)
          symptomControlled: null,
          source: "patient",
          comments: "",
          overrideDate: new Date(),
          commentsToClinicalAlert: false,
          status: "active",
        },
      ],
    };
    // createMedicalCondition accepts a third arg `fromClinicalService` which suppresses its toast when true
    dispatch(createMedicalCondition(patient.id, currentCondition, true));
  };

  const handleStatusChange = (value) => {
    patient.status = value;
    setStatus(value);
    setStatusReason(getStatusReason());
  };

  useEffect(() => {
    if (clicked) {
      fieldsToCheck.forEach(({ field, checkFn, setErrorFn, alter }) => {
        const hasError = alter ? !checkFn(field) : checkFn(field);
        setErrorFn(hasError);
      });
    }
  }, [clicked, ...fieldsToCheck.map(({ field }) => field)]);

  return (
    <CommonSlider open={open} onClose={onClose}>
      <div className="flex flex-col flex-1 h-full overflow-y-auto ">
        <div className="relative flex-1 flex flex-col">
          {/* Header */}
          <div className="px-8 py-6 bg-gray-20">
            <div className="flex items-start justify-between space-x-3">
              <div className="flex flex-col">
                <p className="font-bold text-gray-800 text-display-xs">
                  {"Edit "} Personal Information
                </p>
                <p className="text-body-md text-gray-700">
                  {lastName}, {firstName}
                </p>
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                  onClick={() => onClose(false)}
                >
                  <span className="sr-only">Close panel</span>
                  <XMarkIcon
                    className="w-5 h-5 m-auto"
                    strokeWidth="2"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
          <div className="px-8 flex flex-col flex-1 justify-between mt-4">
            <div className="grid grid-cols-12 gap-3">
              <CustomInput
                name="lastName"
                className="col-span-4"
                label="Last Name"
                placeholder="Enter last name"
                type="text"
                value={lastName}
                required
                onChange={(e) => setLastName(e.target.value)}
                error={lastNameError}
                autoFocus={lastNameError}
                focus={lastNameError}
                capitalizeFirstLetter={true}
              />
              <CustomInput
                name="middleName"
                className="col-span-4"
                label="Middle Name"
                placeholder="Enter middle name"
                type="text"
                value={middleName}
                capitalizeFirstLetter={true}
                onChange={(e) => setMiddleName(e.target.value)}
                error={middleName && isNameError(middleName)}
              />
              <CustomInput
                name="firstName"
                className="col-span-4"
                label="First Name"
                placeholder="Enter first name"
                type="text"
                required
                value={firstName}
                capitalizeFirstLetter={true}
                onChange={(e) => setFirstName(e.target.value)}
                error={firstNameError}
              />
              <div className="col-span-6">
                <CustomInput
                  name="dateOfBirth"
                  label="Date of Birth"
                  type="date"
                  value={dateOfBirth}
                  required
                  disableFuture={true}
                  onChange={(value) => setDateOfBirth(value)}
                  error={dateOfBirthError}
                />
              </div>
              <CustomInput
                name="age"
                className="col-span-6"
                placeholder={""}
                label="Age"
                type="text"
                disabled
                showOptionalLabel={false}
                required={true}
                value={calculateAge(dateOfBirth)}
              />
              <CustomInput
                options={biologicalSexOptions}
                name="biologicalSex"
                className="col-span-6"
                label="Biological sex"
                value={
                  biologicalSexOptions?.find((g) => g.value === biologicalSex)
                    ?.value || "preferNotToRespond"
                }
                type="dropdown"
                onChange={(e) => setBiologicalSex(e.value)}
              />
              <CustomInput
                options={raceOptions}
                name="race"
                label="Race"
                className="col-span-6"
                type="dropdown"
                value={race}
                onChange={(e) => setRace(e.value)}
              />
              <CustomInput
                options={genderOptions}
                name="gender"
                label="Gender"
                className="col-span-12"
                type="dropdown"
                value={
                  genderOptions?.find((g) => g.value === gender)?.value ||
                  "preferNotToRespond"
                }
                onChange={(e) => setGender(e.value)}
              />
              <CustomInput
                options={primaryLanguageOptions}
                name="primaryLanguage"
                className="col-span-6"
                label="Primary Language"
                type="dropdown"
                required
                value={primaryLanguage}
                onChange={(e) => setPrimaryLanguage(e.value)}
                error={primaryLanguageError}
              />
              <CustomInput
                options={preferencesOptions}
                name="preferences"
                className="col-span-6"
                value={preferences}
                label="Preferences"
                type="multi-dropdown"
                exclusiveSelectValues={["none"]}
                onChange={(selected) =>
                  setPreferences(
                    normalizePreferencesSelection(preferences, selected)
                  )
                }
              />
              <CustomInput
                options={medicationAdministrationOptions}
                name="medicationAdministration"
                label="Medication Administration"
                className="col-span-6"
                type="dropdown"
                value={medicationAdministration}
                onChange={(e) => setMedicationAdministration(e.value)}
                error={administrationError}
                required
              />
              <CustomInput
                options={characteristicsOptions.map((x) => {
                  return { label: x.header, value: x.value };
                })}
                name="characteristics"
                className="col-span-6"
                value={characteristics}
                label="Characteristics"
                type="multi-dropdown"
                onChange={(selected) =>
                  setCharacteristics(
                    normalizeCharacteristicsSelection(characteristics, selected)
                  )
                }
                error={characteristicsError}
                required
              />
              <CustomInput
                options={patientEditStatusOptions}
                name={`status`}
                value={status}
                className="col-span-6"
                label="Status"
                type="dropdown"
                onChange={(e) => handleStatusChange(e.value)}
                required
                error={statusError}
              />
              <CustomInput
                options={patientEditInactiveStatusReasonOptions}
                name={`statusReason`}
                value={statusReason}
                className="col-span-6"
                label="Status Reason"
                type="dropdown"
                onChange={(e) => setStatusReason(e.value)}
                disabled={status === "active"}
                required
                error={statusReasonError}
              />
            </div>
            <div className="flex items-center justify-center py-6">
              <div className="flex items-center justify-center space-x-5">
                <Button
                  className="min-w-[200px]"
                  text={`Cancel`}
                  type="secondary"
                  onClick={() => {
                    onClose(false);
                  }}
                />
                <Button
                  className="min-w-[200px]"
                  text="Update"
                  type="primary"
                  onClick={() => manageStep()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </CommonSlider>
  );
};

export default PersonalInformationCreate;
